// lazyWithReload — a drop-in replacement for React.lazy that survives
// a deploy happening while a tab is open.
//
// THE PROBLEM IT FIXES (Sentry JAVASCRIPT-REACT-P, 2026-06-29):
// Every route in this app is code-split — `lazy(() => import("./pages/X"))`
// downloads a content-hashed chunk (e.g. ProfilePage-BUOf4uqS.js) the
// first time the user navigates to it. When we ship a new build, Pages
// replaces those chunks with NEW hashes and deletes the old ones. A user
// who loaded the app BEFORE the deploy still holds the old index.html in
// their tab, so the moment they navigate to a not-yet-downloaded route,
// the dynamic import points at a hash that no longer exists on the
// server → "TypeError: Failed to fetch dynamically imported module" →
// the page throws and the user sees a broken screen.
//
// THE FIX:
// On a chunk-load failure, do a ONE-TIME full page reload. A reload
// re-fetches index.html, which references the current chunk hashes, so
// the navigation then succeeds. A sessionStorage guard makes the reload
// fire at most once per failure so a genuinely-missing chunk (offline,
// real 500) can't trap the user in a reload loop — after one reload we
// let the error surface to the boundary / Sentry as before.

import { lazy, type ComponentType, type LazyExoticComponent } from "react";

// sessionStorage (not localStorage): per-tab, and cleared when the tab
// closes — exactly the scope of "this tab is mid-deploy". Survives the
// reload itself (sessionStorage persists across reload within a tab),
// which is what makes the loop guard work.
const RELOAD_GUARD_KEY = "swos:chunk-reload-attempted";

// We reach the browser globals through `globalThis` with a local type,
// rather than the bare `window` global: this file is imported by a test
// in the root tsconfig program, which has no DOM lib. `globalThis` IS
// `window` in the browser, so this is identical at runtime.
interface BrowserGlobals {
  sessionStorage: {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
  };
  location: { reload(): void };
}

const browser = globalThis as unknown as BrowserGlobals;

// The browser surfaces this stale-chunk condition with a few different
// messages depending on engine (Chrome/Safari/Firefox), so match on the
// shared shapes rather than one exact string.
//
// "Unable to preload CSS for ..." is Vite's OWN wording (its dynamic-
// import preload helper), not a browser message — a route chunk's CSS
// file going stale/missing after a deploy is the exact same condition as
// its JS chunk going stale, and it rejects the SAME import() promise
// loadWithReload wraps. Missing this case is how a booking-page visitor
// hit the failure raw instead of getting the one-time reload (Sentry
// JAVASCRIPT-REACT-4A, 2026-09-14).
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /Unable to preload CSS for/i.test(message)
  );
}

// The reload-on-failure core, factored out of `loadWithReload` so a
// plain `await import(...)` that isn't wired through React.lazy — the
// PDF renderer's on-demand `import("pdfjs-dist")`, for one — gets the
// same one-time-reload recovery instead of surfacing the raw fetch
// error to whoever called it. Both callers hit the SAME sessionStorage
// guard, so it stays "reload once per tab" no matter which of them hit
// the stale chunk first.
export async function withChunkReload<T>(factory: () => Promise<T>): Promise<T> {
  try {
    const result = await factory();
    // Loaded cleanly — clear the guard so that a LATER deploy in this
    // same long-lived tab can still trigger its own one-time reload.
    browser.sessionStorage.removeItem(RELOAD_GUARD_KEY);
    return result;
  } catch (error) {
    const alreadyReloaded =
      browser.sessionStorage.getItem(RELOAD_GUARD_KEY) === "1";
    if (isChunkLoadError(error) && !alreadyReloaded) {
      browser.sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
      browser.location.reload();
      // Hold the caller up while the reload navigates away, instead of
      // flashing an error state for the split-second before the page
      // tears down.
      return new Promise<T>(() => {});
    }
    throw error;
  }
}

// The reload-on-failure loader, separated from `lazy()` so the recovery
// logic is unit-testable without rendering a Suspense tree.
export function loadWithReload<T>(
  factory: () => Promise<{ default: T }>,
): () => Promise<{ default: T }> {
  return () => withChunkReload(factory);
}

// The `ComponentType<any>` bound mirrors React's own `lazy` signature
// exactly (anything stricter isn't assignable into `lazy` because of
// class-component prop variance). The concrete component type `T` is
// still inferred at each call site, so prop typing is fully preserved —
// this is a drop-in replacement for `lazy`.
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): LazyExoticComponent<T> {
  return lazy(loadWithReload(factory));
}
